/**
 * Browser delivery for a rendered export. The only DOM-specific step in the
 * pipeline — everything upstream is shared with native and the server.
 */

import { type EventExport, exportDataUrl } from '@project/event-creator';

/**
 * What delivery needs to know about an export. Narrower than `EventExport` on purpose: the
 * hosted site builds a multi-event file of its own, which has a name and a type but no single
 * format id, and it should go out the same way one event does.
 */
export type Deliverable = Pick<EventExport, 'fileName' | 'mimeType' | 'content'>;

function fileFor(payload: Deliverable): File {
  return new File([payload.content], payload.fileName, { type: payload.mimeType });
}

/**
 * Whether this browser can hand the file to another app — the share sheet.
 *
 * It is gated on a coarse pointer because this helper is for an explicit phone Share action. On a
 * desktop the direct download is the expected route, and desktop Chrome would otherwise answer
 * `canShare` with a yes and replace a saved file with a dialog nobody asked for.
 */
export function canShareExport(payload: Deliverable): boolean {
  if (typeof navigator === 'undefined' || typeof navigator.share !== 'function') return false;
  if (typeof navigator.canShare !== 'function' || typeof File !== 'function') return false;
  if (typeof matchMedia === 'function' && !matchMedia('(pointer: coarse)').matches) return false;
  try {
    return navigator.canShare({ files: [fileFor(payload)] });
  } catch {
    return false;
  }
}

/**
 * Offers the file to another app. Resolves `false` when the sheet was never an option, so the
 * caller can fall back to a download; a share the user cancelled resolves `true`, because
 * declining to share is a decision and pushing a download on top of it is not the answer.
 */
export async function shareExport(payload: Deliverable): Promise<boolean> {
  // Called before any `await` so the share still counts as happening inside the tap — iOS
  // rejects one that doesn't.
  if (!canShareExport(payload)) return false;
  try {
    await navigator.share({ files: [fileFor(payload)], title: payload.fileName });
    return true;
  } catch (error) {
    return error instanceof Error && error.name === 'AbortError';
  }
}

/**
 * The default way a finished event leaves the page: open/download the generated file directly.
 *
 * Mobile Safari recognises a `text/calendar` download and can hand it straight to the native
 * Calendar importer. Sending the same file through `navigator.share` inserts the share sheet in
 * front of that system experience, so sharing remains an explicit option rather than the default.
 */
export function deliverExport(payload: Deliverable, doc: Document = document): void {
  downloadExport(payload, doc);
}

/** Saves the export as a file, falling back to a data URL where `Blob` isn't available. */
export function downloadExport(payload: Deliverable, doc: Document = document): void {
  const anchor = doc.createElement('a');
  anchor.download = payload.fileName;
  const url =
    typeof URL.createObjectURL === 'function'
      ? URL.createObjectURL(new Blob([payload.content], { type: payload.mimeType }))
      : exportDataUrl(payload);
  anchor.href = url;
  doc.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  if (url.startsWith('blob:')) URL.revokeObjectURL(url);
}

/** Copies the rendered text; resolves `false` when the clipboard is unavailable or denied. */
export async function copyExport(payload: Deliverable): Promise<boolean> {
  if (typeof navigator === 'undefined' || navigator.clipboard === undefined) return false;
  try {
    await navigator.clipboard.writeText(payload.content);
    return true;
  } catch {
    return false;
  }
}

/** The host's zone, for prefilling a new draft. */
export function localTimeZone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return undefined;
  }
}

/**
 * The next half hour on the clock: 16:05 → 16:30, 16:30 → 17:00. A form that opens on today at
 * 09:00 has already suggested a time that passed hours ago, and the first thing anyone does is
 * fix it. Capped at 23:30 rather than rolling into tomorrow, which would contradict the date
 * beside it.
 */
export function nextHalfHour(now: Date = new Date()): string {
  const minutes = now.getHours() * 60 + now.getMinutes();
  const rounded = Math.min((Math.floor(minutes / 30) + 1) * 30, 23 * 60 + 30);
  const pad = (value: number): string => String(value).padStart(2, '0');
  return `${pad(Math.floor(rounded / 60))}:${pad(rounded % 60)}`;
}

/** Today's local date as `YYYY-MM-DD` — the packages have no clock of their own. */
export function todayLocal(now: Date = new Date()): string {
  const pad = (value: number): string => String(value).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}
